/**
 * WallSketch Selection
 *
 * Owns the per-tool selection state (selected node ids + selected edge ids)
 * for tools that work against a `PointoramaWallSketch`, plus the matching
 * visual overlays:
 *   - cyan border + tint on each selected node marker
 *   - cyan LineSegments2 overlay along each selected edge
 *
 * Lifted from `WallSketchEditSession` so the door/window tool can reuse the
 * same selection model + visuals without duplicating the bookkeeping.
 *
 * Coupling notes:
 *  - The visuals come from the skeleton itself (`getNodeMarker(id)` for the
 *    border, plus a free-floating `LineSegments2` parented to the scene
 *    graph). The class does not assume any particular tool lifecycle —
 *    consumers call `setSelection / addToSelection / removeFromSelection`
 *    and the visuals follow.
 *  - When a node is deselected we restore its color to the
 *    `deselectedNodeColor` supplied at construction time. The skeleton
 *    editor passes its edit-mode tint there; the door/window tool passes
 *    the skeleton's own line color so deselection looks like "default."
 *  - The class does NOT call into a context menu — selection-change side
 *    effects (closing an empty-selection context menu, refreshing other
 *    UI) belong to the consumer and run via the `onSelectionChanged`
 *    callback passed into the constructor.
 */

import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { Viewer } from '../../../../viewer/viewer';
import { PointoramaWallSketch } from '../../../../utils/PointoramaWallSketch';
import { tessellateEdge } from '../../../../utils/skeleton/arcGeometry';

/**
 * Selection highlight — the primary brand green (neon-green-500, #7EDD64).
 * Distinct from the edit-mode node/edge color (french violet) so a selected
 * element still reads clearly. Exported so tools can match.
 */
export const SKELETON_SELECTION_COLOR = new THREE.Color('#7EDD64');

export interface WallSketchSelectionDeps {
  viewer: Viewer;
  /** Resolve the active skeleton on demand. Returning null = no visuals to apply. */
  getWallSketch: () => PointoramaWallSketch | null;
  /**
   * Color to restore on a node marker when it's removed from the selection.
   * WallSketch edit mode tints all markers orange; the door/window tool keeps
   * them at the skeleton's own line color. Defaults to white if omitted.
   */
  deselectedNodeColor?: () => THREE.Color;
  /**
   * Called whenever the selection contents change — both terminal calls
   * (`setSelection`) and incremental ones (`addToSelection`,
   * `removeFromSelection`, `clearStale`). The consumer is expected to
   * close empty-selection context menus, refresh dependent toolbars, etc.
   */
  onSelectionChanged?: () => void;
  /** Hex color override for the selection visuals. Defaults to cyan 0x00ddff. */
  highlightColor?: THREE.Color;
}

/**
 * Normalize a "selection id input" — either a single id, an iterable of ids,
 * or null — into a Set. Lets callers hand in any of the three forms
 * without ceremony.
 */
function toIdSet(input: Iterable<string> | string | null | undefined): Set<string> {
  if (input == null) return new Set();
  if (typeof input === 'string') return new Set([input]);
  return new Set(input);
}

export class WallSketchSelection {
  private deps: WallSketchSelectionDeps;
  private readonly highlightColor: THREE.Color;

  /** When true, no selection visuals render (state + change notifications are
   * unaffected). Tools that draw their own selection visual — the
   * door/window tool's wall-strip overlay — enable this while active. */
  private visualsSuppressed = false;

  // Selection state — multi-select via shift/ctrl click or box-drag. The
  // sets are independent: a selection can hold any mix of nodes and edges.
  private selectedNodeIds: Set<string> = new Set();
  private selectedEdgeIds: Set<string> = new Set();

  // Multi-edge selection overlay. Uses LineSegments2 (one geometry, many
  // disconnected segments) so the same mesh can render every selected edge
  // in a single draw call. Lazily created on first use.
  private selectionEdgeLine: LineSegments2 | null = null;
  private selectionEdgeMaterial: LineMaterial | null = null;

  constructor(deps: WallSketchSelectionDeps) {
    this.deps = deps;
    this.highlightColor = deps.highlightColor ?? SKELETON_SELECTION_COLOR;
  }

  /**
   * Override the deselected-node color callback at runtime. The skeleton edit
   * session uses this to swap in its orange edit-mode tint while editing,
   * then swap it back (pass `undefined`) on exit. Newly-deselected nodes
   * pick up the new color; any currently-deselected nodes keep their
   * present visual until the next mutation triggers a repaint — call
   * `reapplyVisuals()` after this if you need an immediate refresh.
   */
  setDeselectedNodeColor(callback: (() => THREE.Color) | undefined): void {
    this.deps = { ...this.deps, deselectedNodeColor: callback };
  }

  // ─── Read access ────────────────────────────────────────────────────

  /** Read-only view of selected node ids. Returns the live set — do not mutate. */
  getSelectedNodeIds(): ReadonlySet<string> {
    return this.selectedNodeIds;
  }

  /** Read-only view of selected edge ids. Returns the live set — do not mutate. */
  getSelectedEdgeIds(): ReadonlySet<string> {
    return this.selectedEdgeIds;
  }

  /** Total number of selected items (nodes + edges). */
  size(): number {
    return this.selectedNodeIds.size + this.selectedEdgeIds.size;
  }

  isEmpty(): boolean {
    return this.size() === 0;
  }

  hasNode(id: string): boolean {
    return this.selectedNodeIds.has(id);
  }

  hasEdge(id: string): boolean {
    return this.selectedEdgeIds.has(id);
  }

  // ─── Mutation ───────────────────────────────────────────────────────

  /**
   * Replace the selection. Pass node/edge id collections (or null for
   * "single", or empty arrays for "clear"). Visuals are cleared and
   * reapplied. For incremental changes, prefer `addToSelection` /
   * `removeFromSelection`.
   */
  setSelection(nodeIds: Iterable<string> | string | null, edgeIds: Iterable<string> | string | null): void {
    this.clearVisuals();
    this.selectedNodeIds = toIdSet(nodeIds);
    this.selectedEdgeIds = toIdSet(edgeIds);
    this.reapplyVisuals();
    this.deps.onSelectionChanged?.();
  }

  /** Add ids to the current selection without dropping anything. */
  addToSelection(nodeIds: Iterable<string>, edgeIds: Iterable<string>): void {
    let changed = false;
    for (const id of nodeIds) {
      if (!this.selectedNodeIds.has(id)) {
        this.selectedNodeIds.add(id);
        changed = true;
      }
    }
    for (const id of edgeIds) {
      if (!this.selectedEdgeIds.has(id)) {
        this.selectedEdgeIds.add(id);
        changed = true;
      }
    }
    if (changed) {
      this.reapplyVisuals();
      this.deps.onSelectionChanged?.();
    }
  }

  /** Remove ids from the current selection. Visuals reset to non-selected. */
  removeFromSelection(nodeIds: Iterable<string>, edgeIds: Iterable<string>): void {
    // Reset the visuals on items we're about to deselect — reapplyVisuals
    // only sets visuals on currently-selected ids, so deselected ones need an
    // explicit unhighlight first.
    const skeleton = this.deps.getWallSketch();
    if (skeleton) {
      const restoreColor = this.deps.deselectedNodeColor?.() ?? new THREE.Color(0xffffff);
      for (const id of nodeIds) {
        if (!this.selectedNodeIds.has(id)) continue;
        const marker = skeleton.getNodeMarker(id);
        if (marker) {
          marker.hideBorder();
          marker.setColor(restoreColor);
        }
      }
    }
    let changed = false;
    for (const id of nodeIds) if (this.selectedNodeIds.delete(id)) changed = true;
    for (const id of edgeIds) if (this.selectedEdgeIds.delete(id)) changed = true;
    if (changed) {
      this.reapplyVisuals();
      this.deps.onSelectionChanged?.();
    }
  }

  /**
   * Drop ids that no longer exist on the skeleton. Call after a topology
   * mutation that may have removed selected items. Notifies the consumer
   * via `onSelectionChanged` only when something actually changed.
   */
  clearStale(deletedNodeId: string | null, deletedEdgeId: string | null): void {
    let changed = false;
    if (deletedNodeId && this.selectedNodeIds.delete(deletedNodeId)) changed = true;
    if (deletedEdgeId && this.selectedEdgeIds.delete(deletedEdgeId)) changed = true;
    if (changed) {
      this.reapplyVisuals();
      this.deps.onSelectionChanged?.();
    }
  }

  /** Clear the selection without notifying the consumer. Used during exit/dispose. */
  clearSilent(): void {
    this.clearVisuals();
    this.selectedNodeIds.clear();
    this.selectedEdgeIds.clear();
  }

  /**
   * Enable/disable visual suppression. Suppressing clears any current
   * visuals; un-suppressing reapplies them from the current selection.
   */
  setVisualsSuppressed(suppressed: boolean): void {
    if (this.visualsSuppressed === suppressed) return;
    this.visualsSuppressed = suppressed;
    if (suppressed) this.clearVisuals();
    else this.reapplyVisuals();
  }

  /**
   * Re-render the selection visuals against the current skeleton. Call
   * after a `skeleton.setData()` rebuild — the previous markers + line
   * geometry are invalidated and need to be reapplied to the new ones.
   */
  reapplyVisuals(): void {
    if (this.visualsSuppressed) return;
    const skeleton = this.deps.getWallSketch();
    if (!skeleton) return;
    for (const nodeId of this.selectedNodeIds) {
      const marker = skeleton.getNodeMarker(nodeId);
      if (marker) {
        marker.showBorder();
        // Match the selected-edge highlight color so node + edge selection
        // read as the same visual concept.
        marker.setColor(this.highlightColor);
      }
    }
    if (this.selectedEdgeIds.size > 0) {
      this.updateSelectionEdgeOverlay(skeleton);
    } else {
      this.hideSelectionEdgeOverlay();
    }
  }

  /**
   * Hide the visual overlays without clearing the underlying id sets. Used
   * when the consumer's mode changes (e.g. exit edit mode) but the
   * selection should be preserved for re-entry.
   */
  clearVisuals(): void {
    const skeleton = this.deps.getWallSketch();
    if (skeleton) {
      const restoreColor = this.deps.deselectedNodeColor?.() ?? new THREE.Color(0xffffff);
      for (const nodeId of this.selectedNodeIds) {
        const marker = skeleton.getNodeMarker(nodeId);
        if (marker) {
          marker.hideBorder();
          marker.setColor(restoreColor);
        }
      }
    }
    this.hideSelectionEdgeOverlay();
  }

  /** Tear down the LineSegments2 overlay. Call from the consumer's dispose path. */
  dispose(): void {
    this.disposeSelectionEdgeOverlay();
    this.selectedNodeIds.clear();
    this.selectedEdgeIds.clear();
  }

  // ─── Internal: edge overlay ────────────────────────────────────────

  /**
   * Draw a thick highlight along every selected edge. Uses LineSegments2 so
   * all selected edges share one mesh (one draw call, one shared material).
   * Lazily created on first use; afterwards we just rewrite the geometry
   * when the selection or underlying node positions change.
   */
  private updateSelectionEdgeOverlay(skeleton: PointoramaWallSketch): void {
    const z = skeleton.getFloorHeight() + 0.05; // sit just above the base line
    const positions: number[] = [];
    for (const edgeId of this.selectedEdgeIds) {
      const edge = skeleton.getEdge(edgeId);
      if (!edge) continue;
      const a = skeleton.getNode(edge.startNodeId);
      const b = skeleton.getNode(edge.endNodeId);
      if (!a || !b) continue;
      // LineSegmentsGeometry: each pair of consecutive vertices is one segment.
      // Curved edges tessellate to a polyline; straight edges stay one segment.
      const polyline = tessellateEdge({ x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, edge.bulge);
      for (let i = 0; i + 1 < polyline.length; i++) {
        positions.push(polyline[i].x, polyline[i].y, z, polyline[i + 1].x, polyline[i + 1].y, z);
      }
    }

    if (positions.length === 0) {
      this.hideSelectionEdgeOverlay();
      return;
    }

    if (!this.selectionEdgeLine || !this.selectionEdgeMaterial) {
      const geometry = new LineSegmentsGeometry();
      geometry.setPositions(positions);
      const renderSize = this.deps.viewer.renderer.getSize(new THREE.Vector2());
      this.selectionEdgeMaterial = new LineMaterial({
        color: this.highlightColor.getHex(),
        linewidth: 6,
        resolution: new THREE.Vector2(renderSize.width || 1000, renderSize.height || 1000),
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      });
      this.selectionEdgeLine = new LineSegments2(geometry, this.selectionEdgeMaterial);
      this.selectionEdgeLine.computeLineDistances();
      this.selectionEdgeLine.renderOrder = 998;
      this.selectionEdgeLine.visible = true;
      this.deps.viewer.scene.scene.add(this.selectionEdgeLine);
      return;
    }

    // Recreate geometry with the new segment count — LineSegmentsGeometry's
    // internal attribute arrays are fixed at construction, so swapping the
    // geometry is safer than reusing it when segment count changes.
    const oldGeom = this.selectionEdgeLine.geometry as LineSegmentsGeometry;
    oldGeom.dispose();
    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(positions);
    this.selectionEdgeLine.geometry = geometry;
    this.selectionEdgeLine.computeLineDistances();
    this.selectionEdgeLine.visible = true;
  }

  private hideSelectionEdgeOverlay(): void {
    if (this.selectionEdgeLine) this.selectionEdgeLine.visible = false;
  }

  private disposeSelectionEdgeOverlay(): void {
    if (!this.selectionEdgeLine) return;
    this.deps.viewer.scene.scene.remove(this.selectionEdgeLine);
    this.selectionEdgeLine.geometry.dispose();
    this.selectionEdgeMaterial?.dispose();
    this.selectionEdgeLine = null;
    this.selectionEdgeMaterial = null;
  }
}
